/**
 * Which rows may be dragged past each other, and how a drag is persisted.
 *
 * A row's position is its status, then its orderZone, then its sortOrder. The
 * server rewrites one (status, zone) batch per call, so a drag is persisted as
 * one call per batch.
 *
 * STATUS is a hard boundary: a Suggestion and an Accepted story are in
 * different lists and dragging one past the other is meaningless.
 *
 * ZONE is NOT. Zones order automated fills against hand-added stories, and they
 * are invisible on screen: nothing tells an editor that the row she is dragging
 * past belongs to yesterday's fill. Refusing that drag is indistinguishable
 * from the feature being broken, which is exactly how it was reported. So a
 * cross-zone drag RE-ZONES the row into the zone it was dropped in, the same
 * resolution `Move to top` already uses, and the batch carries its zone so the
 * server writes it.
 *
 * 34 of the 67 buckets holding suggestions in production span more than one
 * zone, including every bucket with a daily automated fill older than a day, so
 * this is the normal case rather than an edge one.
 *
 * Pure, so the rule can be asserted here without a browser or a database.
 */

/**
 * The zone a row belongs to, defaulted the same way the server defaults it.
 * A row written before `orderZone` existed carries none; the backend reads
 * those as STORY_FEED (3) and so must this, or the two disagree about which
 * rows form a block.
 */
export const DEFAULT_ORDER_ZONE = 3;

export function rowOrderZone(row) {
  return Number.isFinite(row?.orderZone) ? row.orderZone : DEFAULT_ORDER_ZONE;
}

/**
 * May one row be dragged past the other? Status only: a cross-zone drag is
 * allowed and re-zones the moved row (see the module note above).
 */
export function canReorderPast(a, b) {
  if (!a || !b) return false;
  return a.status === b.status;
}

/**
 * Split the displayed rows into the batches the reorder endpoint accepts, in
 * display order. One batch per (status, zone).
 *
 * Each batch carries its `orderZone` so the caller can tell the server which
 * zone to write. A row that was dragged into another zone has already had its
 * local `orderZone` rewritten by the caller, so it groups with its new
 * neighbours here and is persisted into that zone.
 *
 * Single-row batches are dropped: there is nothing to reorder, and the row's
 * zone cannot have changed without it joining an existing zone's rows.
 *
 * @param {Array<object>} items rows in the order currently on screen
 * @returns {Array<{ids: string[], orderZone: number}>} one entry per call to make
 */
export function buildReorderBatches(items) {
  const batches = new Map();
  for (const item of items || []) {
    if (!item?._id) continue;
    const orderZone = rowOrderZone(item);
    const key = `${item.status}::${orderZone}`;
    if (!batches.has(key)) batches.set(key, { ids: [], orderZone });
    batches.get(key).ids.push(String(item._id));
  }
  return [...batches.values()].filter((b) => b.ids.length > 1);
}
